import { FormControl, FormField, FormItem, FormLabel } from '@/components/ui-kit/form';
import { useTranslation } from 'react-i18next';
import { useFormContext, get } from 'react-hook-form';
import { ColorSwatch } from '@/components/color-swatch/color-swatch';
import { Button } from '@/components/ui-kit/button';
import { ChatbotWidget } from '../widgets/chatbot/index.type';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui-kit/accordion';
import { ColorSuggestionForm } from '../color-suggestion';
import { useEffect, useState } from 'react';
import { WidgetColorPalette } from '@/modules/gpt-chats/models/widget.model';

export const AgentIntegrationChatbotDesign = () => {
  const { t } = useTranslation();
  const [accordionValue, setAccordionValue] = useState(['']);
  const form = useFormContext<ChatbotWidget>();

  const handleSuggestions = ({
    values,
    suggestion,
  }: {
    values: { url: string; brand_color: string };
    suggestion: WidgetColorPalette;
  }) => {
    form.setValue(
      'designSettings.headerStartColor',
      suggestion['chat-header--background-start-color']
    );
    form.setValue('designSettings.headerEndColor', suggestion['chat-header--background-end-color']);
    form.setValue('designSettings.titleColor', suggestion['chat-header--title-color']);
    form.setValue('designSettings.borderColor', suggestion['chat--border-color']);
    form.setValue(
      'designSettings.chatBackgroundColor',
      suggestion['chat-window--background-color']
    );
    form.setValue('designSettings.chatFontColor', suggestion['chat-window--font-color']);
    form.setValue(
      'designSettings.sendButtonBackgroundColor',
      suggestion['send-button--background-color']
    );
    form.setValue('designSettings.sendButtonFontColor', suggestion['send-button--font-color']);
    form.setValue(
      'designSettings.chat-toggle-button--background-color',
      suggestion['chat-toggle-button--background-color']
    );
    form.setValue(
      'designSettings.chat-toggle-button--color',
      suggestion['chat-toggle-button--color']
    );
    form.setValue('site_url', values.url);
    form.setValue('brand_color', values.brand_color, { shouldDirty: true });
    setAccordionValue(['header', 'window', 'toggle-button']);
  };

  useEffect(() => {
    const fields = [
      'designSettings.headerStartColor',
      'designSettings.headerEndColor',
      'designSettings.titleColor',
      'designSettings.chatBackgroundColor',
      'designSettings.chatFontColor',
      'designSettings.sendButtonBackgroundColor',
      'designSettings.sendButtonFontColor',
      'designSettings.borderColor',
    ] as const;

    fields.forEach((field) => {
      form.register(field);
    });
  }, [form]);

  const { isValid } = form.formState;
  return (
    <div className="grid gap-6">
      <ColorSuggestionForm
        values={{
          url: form.getValues('site_url') || '',
          brand_color: form.getValues('brand_color') || '',
        }}
        onSuggestion={handleSuggestions}
      />
      <Accordion type="multiple" value={accordionValue} onValueChange={setAccordionValue}>
        <AccordionItem value="header" className="border-b-0">
          <AccordionTrigger className="hover:no-underline">{t('CHAT_HEADER')}</AccordionTrigger>
          <AccordionContent>
            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="designSettings.headerStartColor"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>{t('START_COLOR')}</FormLabel>
                      <FormControl>
                        <ColorSwatch
                          {...field}
                          hasError={!!get(form.formState.errors, field.name)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="designSettings.headerEndColor"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>{t('END_COLOR')}</FormLabel>
                      <FormControl>
                        <ColorSwatch
                          {...field}
                          hasError={!!get(form.formState.errors, field.name)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="designSettings.titleColor"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('TITLE_COLOR')}</FormLabel>
                    <FormControl>
                      <ColorSwatch {...field} hasError={!!get(form.formState.errors, field.name)} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="window" className="border-b-0">
          <AccordionTrigger className="hover:no-underline">{t('CHAT_STYLE')}</AccordionTrigger>
          <AccordionContent>
            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="designSettings.chatBackgroundColor"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>{t('BACKGROUND_COLOR')}</FormLabel>
                      <FormControl>
                        <ColorSwatch
                          {...field}
                          hasError={!!get(form.formState.errors, field.name)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="designSettings.chatFontColor"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>{t('FONT_COLOR')}</FormLabel>
                      <FormControl>
                        <ColorSwatch
                          {...field}
                          hasError={!!get(form.formState.errors, field.name)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="designSettings.sendButtonBackgroundColor"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>{t('BUTTON_COLOR')}</FormLabel>
                      <FormControl>
                        <ColorSwatch
                          {...field}
                          hasError={!!get(form.formState.errors, field.name)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="designSettings.sendButtonFontColor"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>{t('ICON_COLOR')}</FormLabel>
                      <FormControl>
                        <ColorSwatch
                          {...field}
                          hasError={!!get(form.formState.errors, field.name)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="designSettings.borderColor"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>{t('BORDER_COLOR')}</FormLabel>
                    <FormControl>
                      <ColorSwatch {...field} hasError={!!get(form.formState.errors, field.name)} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="toggle-button" className="border-b-0">
          <AccordionTrigger className="hover:no-underline">{t('TOGGLE_BUTTON')}</AccordionTrigger>
          <AccordionContent>
            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="designSettings.chat-toggle-button--background-color"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>{t('BACKGROUND_COLOR')}</FormLabel>
                      <FormControl>
                        <ColorSwatch
                          {...field}
                          hasError={!!get(form.formState.errors, field.name)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="designSettings.chat-toggle-button--color"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>{t('ICON_COLOR')}</FormLabel>
                      <FormControl>
                        <ColorSwatch
                          {...field}
                          hasError={!!get(form.formState.errors, field.name)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Button className="w-full" type="submit" disabled={!isValid}>
        {t('SAVE')}
      </Button>
    </div>
  );
};
